import { useLayoutEffect, useRef, type ReactNode, type RefObject } from 'react'
import { getBottomPadding } from '../../../utils/sessionStorage'
import { useElitePlansLanding } from '../ElitePlansLandingContext'
import { useElitePlansIsDarkMode } from '../hooks/useElitePlansIsDarkMode'
import { ElitePlansStatusErrorScreen } from './ElitePlansStatusErrorScreen'

/**
 * Shared viewport scroll root for elite-plans webviews (body/html are overflow-hidden).
 * `isContentScrollOwner`: shell becomes a fixed-height column and the page body scrolls itself
 * (`/elite-plans-family-landing`).
 */
export function ElitePlansPageShell({
  children,
  isContentScrollOwner = false,
  scrollRootRef: scrollRootRefFromParent,
  /** Extra bottom inset so fixed CTA + About FAB don’t cover the footer. */
  bottomInsetPx = 24
}: {
  children: ReactNode
  isContentScrollOwner?: boolean
  scrollRootRef?: RefObject<HTMLDivElement | null>
  bottomInsetPx?: number
}) {
  const { activeTab, isStatusError, refetchStatus } = useElitePlansLanding()
  const isDarkMode = useElitePlansIsDarkMode()
  const localScrollRootRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const scrollRoot = scrollRootRefFromParent?.current ?? localScrollRootRef.current
    scrollRoot?.scrollTo({ top: 0, left: 0 })
  }, [activeTab.id, scrollRootRefFromParent])

  const assignScrollRootRef = (node: HTMLDivElement | null) => {
    localScrollRootRef.current = node
    if (scrollRootRefFromParent) {
      scrollRootRefFromParent.current = node
    }
  }

  // Status API failed (after query retry) — full-screen error on every elite-plans page
  if (isStatusError) {
    return <ElitePlansStatusErrorScreen themeId={activeTab.themeId} onRetry={refetchStatus} />
  }

  if (isContentScrollOwner) {
    return (
      <div
        className='elite-plans-root flex h-screen flex-col overflow-hidden'
        data-plan={activeTab.themeId}
        data-display-mode={isDarkMode ? 'dark' : 'light'}
      >
        <div className='mx-auto flex min-h-0 w-full max-w-[720px] flex-1 flex-col lg:max-w-[880px]'>
          {children}
        </div>
      </div>
    )
  }

  return (
    <div
      ref={assignScrollRootRef}
      className='elite-plans-root h-screen overflow-y-auto overflow-x-hidden no-scrollbar overscroll-none'
      data-plan={activeTab.themeId}
      data-display-mode={isDarkMode ? 'dark' : 'light'}
      style={{
        paddingBottom: `calc(${getBottomPadding()}px + ${bottomInsetPx}px)`
      }}
    >
      <div className='mx-auto w-full max-w-[720px] lg:max-w-[880px]'>{children}</div>
    </div>
  )
}
